import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Headphones,
  Smartphone,
  Building2,
  Heart,
  Users,
  UserCheck,
  Leaf
} from 'lucide-react';
import footerLogo from '../../assets/logo6.png';
import playstore from '../../assets/playstore.webp';
import appstore from '../../assets/appstore.webp';
import { mainAdminApi } from '../../config';

// Official MetaFit store URLs
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.metafitwellness&hl=en_IN';
const APP_STORE_URL = 'https://apps.apple.com/us/app/metafit-wellness/id6759083882';

interface PillarCategory {
  _id: string;
  name: string;
  isActive: boolean;
}

// Graceful Lotus Flower SVG Icon for PRACTICES header (matching reference)
const LotusIcon = ({ className = 'w-5 h-5 text-[#F2B544]' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    {/* Center tall petal */}
    <path d="M12 3C11.3 6 9.5 8.8 7 10.8C9.5 11.5 12 11.5 12 11.5C12 11.5 14.5 11.5 17 10.8C14.5 8.8 12.7 6 12 3Z" />
    {/* Left middle petal */}
    <path d="M5.5 11.8C3.2 13.2 1.8 15.6 2.5 17.6C4.5 18 7.5 17 10 15C8.2 14.1 6.6 13 5.5 11.8Z" />
    {/* Right middle petal */}
    <path d="M18.5 11.8C20.8 13.2 22.2 15.6 21.5 17.6C19.5 18 16.5 17 14 15C15.8 14.1 17.4 13 18.5 11.8Z" />
    {/* Bottom base curve */}
    <path d="M6.8 18.2C8.3 19.3 10.1 19.8 12 19.8C13.9 19.8 15.7 19.3 17.2 18.2C15 17.3 9 17.3 6.8 18.2Z" opacity="0.9" />
  </svg>
);

const Footer: React.FC = () => {
  const [pillars, setPillars] = useState<PillarCategory[]>([]);

  // Fetch pillar categories (Practices)
  useEffect(() => {
    let isMounted = true;
    const fetchPillars = async () => {
      try {
        const response = await mainAdminApi.get<any>('/pillar-categories');
        if (isMounted && response.data && response.data.categories) {
          setPillars(response.data.categories.filter((p: PillarCategory) => p.isActive));
        }
      } catch (error) {
        console.error('Error fetching footer pillars:', error);
      }
    };
    fetchPillars();
    return () => {
      isMounted = false;
    };
  }, []);

  const defaultPractices = [
    { name: 'Guide', link: '/#pillars' },
    { name: 'Recover', link: '/#pillars' },
    { name: 'Nourish', link: '/#pillars' },
    { name: 'Heal', link: '/#pillars' },
    { name: 'Move', link: '/#pillars' },
    { name: 'Breathe', link: '/#pillars' },
    { name: 'Treatments & Retreats', link: '/#retreats' },
  ];

  const destinations = [
    { name: 'Udaipur', link: '/yoga-instructor?city=Udaipur' },
    { name: 'Rishikesh', link: '/luxury-wellness-retreats' },
    { name: 'Goa', link: '/luxury-wellness-retreats' },
    { name: 'Jaipur', link: '/yoga-instructor?city=Jaipur' },
    { name: 'Mumbai', link: '/yoga-instructor?city=Mumbai' },
    { name: 'Bangalore', link: '/yoga-instructor?city=Bangalore' },
    { name: 'Delhi NCR', link: '/yoga-instructor?city=Delhi' },
    { name: 'Online (India)', link: '/classes' },
  ];

  return (
    <footer className="theme-footer relative bg-[#092219] text-[#F7F1E5] pt-14 sm:pt-16 lg:pt-20 pb-8 overflow-hidden select-none border-t border-[#F2B544]/20">
      {/* Background Decorative Ambient Glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background: 'radial-gradient(circle at 15% 20%, rgba(26, 77, 58, 0.45) 0%, transparent 55%), radial-gradient(circle at 85% 80%, rgba(212, 167, 73, 0.15) 0%, transparent 60%)'
        }}
      />

      <div className="relative max-w-[1340px] mx-auto px-5 sm:px-8 z-10">
        {/* Main Footer 6-Column Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.35fr_0.9fr_0.95fr_0.9fr_0.95fr_1.1fr] gap-8 lg:gap-6 xl:gap-8 mb-12">
          
          {/* Column 1 — Brand and Corporate Office */}
          <div className="flex flex-col items-start pr-0 lg:pr-2">
            <Link to="/" className="inline-block mb-4 hover:opacity-95 transition-opacity" aria-label="MetaFit Wellness Home">
              <div className="w-[82px] h-[82px] rounded-2xl bg-white p-2.5 shadow-lg flex items-center justify-center border border-white/20">
                <img
                  src={footerLogo}
                  alt="MetaFit Wellness Logo"
                  width="72"
                  height="72"
                  className="w-full h-full object-contain"
                />
              </div>
            </Link>

            <p className="text-[13.5px] leading-[1.6] text-[#E0EDE7] font-normal mb-5 max-w-[270px]">
              A home for every kind of practitioner — and every kind of practice.
            </p>

            <div className="w-full border-t border-white/10 pt-4 flex flex-col gap-3 max-w-[270px]">
              {/* Corporate Office */}
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#F2B544] shrink-0 mt-0.5" />
                <div className="text-[12px] leading-[1.5] text-[#CFE0D8]">
                  <span className="font-mono font-bold text-[#F2B544] block text-[11px] uppercase tracking-[0.12em] mb-1">
                    CORPORATE OFFICE
                  </span>
                  First Floor, Above Arbuda Electric, Behind Celebration Mall, Plot No. 5, Garh Magri, Ratneshwar Colony, Pulla Bhuwana, Udaipur, Rajasthan 313001
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#F2B544] shrink-0" />
                <a
                  href="tel:+918829912389"
                  className="text-[12.5px] text-[#CFE0D8] hover:text-[#F2B544] transition-colors font-medium"
                >
                  +91 8829912389
                </a>
              </div>

              {/* Email */}
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#F2B544] shrink-0" />
                <a
                  href="mailto:cto.metafit@gmail.com"
                  className="text-[12.5px] text-[#CFE0D8] hover:text-[#F2B544] transition-colors font-medium break-all"
                >
                  cto.metafit@gmail.com
                </a>
              </div>

              {/* Social Media Icons */}
              <div className="flex items-center gap-2.5 pt-2">
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/metafitwellness/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="MetaFit on Instagram"
                  className="w-8 h-8 rounded-full bg-black/25 hover:bg-[#F2B544]/20 border border-white/15 hover:border-[#F2B544] flex items-center justify-center text-white/85 hover:text-[#F2B544] transition-all"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>


                {/* YouTube */}
                <a
                  href="https://www.youtube.com/@metafitwellness"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="MetaFit on YouTube"
                  className="w-8 h-8 rounded-full bg-black/25 hover:bg-[#F2B544]/20 border border-white/15 hover:border-[#F2B544] flex items-center justify-center text-white/85 hover:text-[#F2B544] transition-all"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/meta-fit-wellness"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="MetaFit on LinkedIn"
                  className="w-8 h-8 rounded-full bg-black/25 hover:bg-[#F2B544]/20 border border-white/15 hover:border-[#F2B544] flex items-center justify-center text-white/85 hover:text-[#F2B544] transition-all"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 8.76c-.97 0-1.75-.79-1.75-1.76s.78-1.76 1.75-1.76 1.75.79 1.75 1.76-.78 1.76-1.75 1.76m1.39 9.74v-8.37H5.07v8.37h2.78z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Column 2 — Practices */}
          <div className="flex flex-col">
            <h5 className="flex items-center gap-2 font-mono text-[12.5px] tracking-[0.14em] uppercase text-[#F2B544] font-bold mb-5 whitespace-nowrap">
              <LotusIcon className="w-4 h-4 text-[#F2B544]" />
              <span>Practices</span>
            </h5>
            <ul className="flex flex-col gap-2.5">
              {(pillars.length > 0 ? pillars.slice(0, 6) : defaultPractices.slice(0, 6)).map((practice, idx) => {
                const name = 'name' in practice ? practice.name : '';
                const link = 'link' in practice ? (practice as any).link : '/#pillars';
                return (
                  <li key={idx}>
                    <a
                      href={link}
                      className="group flex items-center justify-between text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5"
                    >
                      <span>{name}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-[#F2B544]/70 group-hover:text-[#F2B544] group-hover:translate-x-0.5 transition-all" />
                    </a>
                  </li>
                );
              })}
              <li>
                <a
                  href="/#retreats"
                  className="group flex items-center justify-between text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5"
                >
                  <span>Treatments &amp; Retreats</span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#F2B544]/70 group-hover:text-[#F2B544] group-hover:translate-x-0.5 transition-all" />
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3 — Destinations */}
          <div className="flex flex-col">
            <h5 className="flex items-center gap-2 font-mono text-[12.5px] tracking-[0.14em] uppercase text-[#F2B544] font-bold mb-5 whitespace-nowrap">
              <MapPin className="w-4 h-4 text-[#F2B544]" />
              <span>Destinations</span>
            </h5>
            <ul className="flex flex-col gap-2.5">
              {destinations.map((dest) => (
                <li key={dest.name}>
                  <Link
                    to={dest.link}
                    className="group flex items-center justify-between text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5"
                  >
                    <span>{dest.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-[#F2B544]/70 group-hover:text-[#F2B544] group-hover:translate-x-0.5 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Company */}
          <div className="flex flex-col">
            <h5 className="flex items-center gap-2 font-mono text-[12.5px] tracking-[0.14em] uppercase text-[#F2B544] font-bold mb-5 whitespace-nowrap">
              <Building2 className="w-4 h-4 text-[#F2B544]" />
              <span>Company</span>
            </h5>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to="/about"
                  className="group flex items-center justify-between text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5"
                >
                  <span>About Us</span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#F2B544]/70 group-hover:text-[#F2B544] group-hover:translate-x-0.5 transition-all" />
                </Link>
              </li>
              <li>
                <Link
                  to="/policy/terms-conditions"
                  className="group flex items-center justify-between text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5"
                >
                  <span>Trust &amp; Safety</span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#F2B544]/70 group-hover:text-[#F2B544] group-hover:translate-x-0.5 transition-all" />
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="group flex items-center justify-between text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5"
                >
                  <span className="flex items-center gap-2">
                    <span>Blog</span>
                    <span className="text-[9.5px] font-mono tracking-wider px-1.5 py-0.5 rounded bg-[#103D2E] text-[#69D19E] border border-[#205A44] uppercase font-bold">
                      Latest
                    </span>
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#F2B544]/70 group-hover:text-[#F2B544] group-hover:translate-x-0.5 transition-all" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 5 — Support */}
          <div className="flex flex-col">
            <h5 className="flex items-center gap-2 font-mono text-[12.5px] tracking-[0.14em] uppercase text-[#F2B544] font-bold mb-5 whitespace-nowrap">
              <Headphones className="w-4 h-4 text-[#F2B544]" />
              <span>Support</span>
            </h5>
            <ul className="flex flex-col gap-3">
              <li>
                <Link
                  to="/contact"
                  className="text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5 inline-block"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/policy/cancellation-refund"
                  className="text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5 inline-block"
                >
                  Cancellations &amp; Refund
                </Link>
              </li>
              <li>
                <Link
                  to="/policy/privacy-policy"
                  className="text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5 inline-block"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/policy/terms-conditions"
                  className="text-[13.5px] text-[#E0EDE7] hover:text-[#F2B544] transition-colors py-0.5 inline-block"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 6 — Get The App */}
          <div className="flex flex-col">
            <h5 className="flex items-center gap-2 font-mono text-[12.5px] tracking-[0.14em] uppercase text-[#F2B544] font-bold mb-5 whitespace-nowrap">
              <Smartphone className="w-4 h-4 text-[#F2B544]" />
              <span>Get The App</span>
            </h5>
            <p className="text-[13px] leading-[1.55] text-[#CFE0D8] font-normal mb-4">
              Practice anywhere. Download the official MetaFit mobile app for iOS &amp; Android.
            </p>

            <div className="flex flex-col gap-3 items-start">
              {/* Google Play Button */}
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
                className="inline-block rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B544]"
              >
                <img
                  src={playstore}
                  alt="Get it on Google Play"
                  width="148"
                  height="44"
                  className="w-[145px] h-auto object-contain block"
                />
              </a>

              {/* Apple App Store Button */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
                className="inline-block rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B544]"
              >
                <img
                  src={appstore}
                  alt="Download on the App Store"
                  width="148"
                  height="44"
                  className="w-[145px] h-auto object-contain block"
                />
              </a>
            </div>
          </div>

        </div>

        {/* Feature Highlights Row (4 Values Strip) */}
        <div className="rounded-2xl border border-white/10 bg-[#0c2f23]/60 backdrop-blur-md px-6 sm:px-8 py-4 my-8 shadow-xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-center">
            
            {/* Feature 1: Holistic Wellness */}
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-[#184533] border border-[#F2B544]/40 flex items-center justify-center text-[#F2B544] shrink-0 shadow-inner">
                <Leaf className="w-5 h-5 fill-[#F2B544]/20 text-[#F2B544]" />
              </div>
              <div className="text-[13.5px] sm:text-[14px] font-medium text-[#F7F1E5] leading-tight">
                Holistic<br />Wellness
              </div>
            </div>

            {/* Feature 2: Expert Practitioners */}
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-[#184533] border border-[#F2B544]/40 flex items-center justify-center text-[#F2B544] shrink-0 shadow-inner">
                <UserCheck className="w-5 h-5 text-[#F2B544]" />
              </div>
              <div className="text-[13.5px] sm:text-[14px] font-medium text-[#F7F1E5] leading-tight">
                Expert<br />Practitioners
              </div>
            </div>

            {/* Feature 3: Mindful Living */}
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-[#184533] border border-[#F2B544]/40 flex items-center justify-center text-[#F2B544] shrink-0 shadow-inner">
                <Heart className="w-5 h-5 fill-[#F2B544] text-[#F2B544]" />
              </div>
              <div className="text-[13.5px] sm:text-[14px] font-medium text-[#F7F1E5] leading-tight">
                Mindful<br />Living
              </div>
            </div>

            {/* Feature 4: Stronger Communities */}
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-[#184533] border border-[#F2B544]/40 flex items-center justify-center text-[#F2B544] shrink-0 shadow-inner">
                <Users className="w-5 h-5 text-[#F2B544]" />
              </div>
              <div className="text-[13.5px] sm:text-[14px] font-medium text-[#F7F1E5] leading-tight">
                Stronger<br />Communities
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Elegant Bottom Wavy Ribbon Layer & Botanical Foliage */}
      <div className="relative w-full mt-6 pt-10 sm:pt-14 pb-4">
        {/* Organic Wave Background SVG */}
        <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden">
          <svg
            className="w-full h-full object-cover"
            viewBox="0 0 1440 180"
            fill="none"
            preserveAspectRatio="none"
          >
            {/* Dark green deep layer */}
            <path
              d="M0,60 C320,130 620,20 980,85 C1180,125 1340,65 1440,45 L1440,180 L0,180 Z"
              fill="url(#waveDarkGreen)"
              opacity="0.8"
            />
            {/* Mid Emerald to Amber ribbon wave */}
            <path
              d="M0,105 C260,35 580,145 920,75 C1140,25 1320,95 1440,80 L1440,180 L0,180 Z"
              fill="url(#waveGoldRibbon)"
              opacity="0.9"
            />
            {/* Bottom glowing Gold ribbon wave */}
            <path
              d="M0,135 C380,95 720,165 1100,110 C1280,80 1380,118 1440,112 L1440,180 L0,180 Z"
              fill="url(#waveGoldGlow)"
            />
            <defs>
              <linearGradient id="waveDarkGreen" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#082017" />
                <stop offset="60%" stopColor="#123d2d" />
                <stop offset="100%" stopColor="#876220" stopOpacity="0.5" />
              </linearGradient>
              <linearGradient id="waveGoldRibbon" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#071d15" />
                <stop offset="30%" stopColor="#194e3a" />
                <stop offset="65%" stopColor="#b48227" />
                <stop offset="100%" stopColor="#ebb750" />
              </linearGradient>
              <linearGradient id="waveGoldGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#082319" />
                <stop offset="45%" stopColor="#1f5842" />
                <stop offset="75%" stopColor="#c59231" />
                <stop offset="100%" stopColor="#f2c055" />
              </linearGradient>
            </defs>
          </svg>

          {/* Botanical Leaves Silhouette Illustration on bottom-right */}
          <div className="absolute right-0 bottom-0 pointer-events-none opacity-30 select-none">
            <svg width="260" height="180" viewBox="0 0 260 180" fill="none">
              <path
                d="M260,180 C230,130 190,90 140,70 C165,65 195,75 220,100 M140,70 C120,40 100,10 90,0 C110,20 120,50 140,70 M160,110 C130,95 105,90 80,95 C105,110 135,115 160,110 M200,140 C170,130 140,135 120,150 C145,155 175,150 200,140"
                stroke="#F2B544"
                strokeWidth="2.5"
                strokeLinecap="round"
                opacity="0.8"
              />
              <path
                d="M140,70 C135,50 140,30 155,15 C155,35 150,55 140,70 Z"
                fill="#F2B544"
                opacity="0.5"
              />
              <path
                d="M175,95 C175,75 185,60 205,50 C200,70 190,85 175,95 Z"
                fill="#F2B544"
                opacity="0.5"
              />
              <path
                d="M110,50 C100,35 102,20 115,10 C120,25 118,40 110,50 Z"
                fill="#F2B544"
                opacity="0.4"
              />
              <path
                d="M130,115 C115,105 110,90 120,75 C130,90 135,105 130,115 Z"
                fill="#F2B544"
                opacity="0.5"
              />
              <path
                d="M170,145 C155,140 148,125 155,110 C168,125 172,138 170,145 Z"
                fill="#F2B544"
                opacity="0.5"
              />
            </svg>
          </div>
        </div>

        {/* Footer Bottom Bar Links & Copyright */}
        <div className="relative max-w-[1340px] mx-auto px-5 sm:px-8 z-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left pt-6 pb-2 text-[12.5px] text-[#F7F1E5]/90 font-medium">
            {/* Copyright */}
            <div>
              &copy; {new Date().getFullYear()} MetaFit Wellness. All rights reserved.
            </div>

            {/* Bottom Nav Links */}
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-3 sm:gap-5 text-[#E0EDE7] text-[12.5px]">
              <Link to="/policy/privacy-policy" className="hover:text-[#F2B544] transition-colors">
                Privacy Policy
              </Link>
              <span className="text-white/30 hidden sm:inline">|</span>
              <Link to="/policy/terms-conditions" className="hover:text-[#F2B544] transition-colors">
                Terms &amp; Conditions
              </Link>
              <span className="text-white/30 hidden sm:inline">|</span>
              <Link to="/policy/cancellation-refund" className="hover:text-[#F2B544] transition-colors">
                Cancellations &amp; Refund
              </Link>
              <span className="text-white/30 hidden sm:inline">|</span>
              <Link to="/contact" className="hover:text-[#F2B544] transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
